import pluto, {
  Brand,
  Child,
  closeSession,
  submitOauth,
} from "@plutoxyz/frame-js";
import { useDemoStore } from "../store/demoStore";
import useOauthStore from "../store/ouathStore";
import { usePromptStore } from "../store/promptStore";
import { ErrorPayload, InitializationOptions } from "@plutoxyz/frame-js";
import { Screen } from "../types/enums";
import { useAuthStore } from "../store/authStore";

type InitializationPayload = {
  origin: string;
  url: string;
  script?: string;
  options: InitializationOptions;
};

/**
 * We have weird casting here because the SDK
 * exports Pluto as a parent instance for external
 * consumers to get correct typing by default,
 * so we just override the typing here internally
 * since this is the only spot we need it.
 */
const _pluto = pluto as unknown as Child;

class Pluto {
  hostOrigin: string;

  constructor() {
    _pluto.initialize();
    _pluto.on("message", (payload) => {
      console.log("Received message", payload);
    });

    _pluto.on("initialized", (payload: InitializationPayload) => {
      this.hostOrigin = payload.origin;

      useDemoStore.setState({
        endpoints: payload.options.endpoints!,
      });

      if (payload.script) {
        useDemoStore.setState({ script: payload.script, initialized: true });
      }

      if (payload.options.brand) {
        useDemoStore.setState({ brand: payload.options.brand });
      }
    });

    _pluto.on("updateBrand", (payload: Brand) => {
      useDemoStore.setState({ brand: payload });
    });

    _pluto.on("oauthSuccess", async (payload) => {
      useOauthStore.setState({ oauthResponse: payload });
      const oauth = useOauthStore.getState().oauth;
      if (!oauth) return;

      try {
        // Todo: gracefully handle 400s
        const result = await submitOauth(
          oauth.request_id,
          payload.code,
          payload.state,
          useAuthStore.getState().sessionId,
          payload.options.endpoints!.notary
        );

        // Only proceed to PENDING screen if the request was successful
        if (result !== null) {
          useAuthStore.setState({ screen: Screen.PENDING });
        }
      } catch (error) {
        await closeSession(
          useAuthStore.getState().sessionId,
          payload.options.endpoints!.notary
        );
      }
    });

    // Handler for recoverable failures (user cancellation)
    _pluto.on("oauthCanceled", (payload) => {
      const { error, errorDescription } = payload;

      useOauthStore.getState().setCancellationError({
        error: error || "oauth_canceled",
        errorDescription:
          errorDescription || "The OAuth authorization was canceled",
      });

      // Keep the OAuth screen visible
      useAuthStore.getState().setScreen(Screen.OAUTH);
    });

    _pluto.on("oauthFailed", (payload) => {
      const { error, errorDescription } = payload;

      usePromptStore.getState().setError({
        code: error || "oauth_error",
        display_message: "OAuth Authorization Failed",
        message:
          errorDescription ||
          "The OAuth provider denied the authorization request",
      });

      useAuthStore.getState().setScreen(Screen.ERROR);
    });
  }

  notifyCdpUrl(url: string) {
    _pluto.notifyCdpUrl(url);
  }

  success(payload: { data: Record<string, string>; signature: string }) {
    _pluto.success(payload);
  }

  error(error: ErrorPayload) {
    _pluto.error(error);
  }

  timeout() {
    _pluto.error({
      code: "timeout",
      display_message: "The session has timed out. Please try again.",
      message: "Session timed out",
    });
  }

  scriptLog(log: string) {
    _pluto.scriptLog(log);
  }

  requestOauth(authCodeUrl: string) {
    _pluto.requestOauth(authCodeUrl);
  }

  getFingerprint() {
    return _pluto.getFingerprint();
  }
}

export default new Pluto();
